import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Link, Stack, Typography } from '@mui/material';
import HybridSeedName from '/@/components/crop-monitoring/hybrid-seed-name';
import PreceonBadge from '/@/components/crop-monitoring/preceon-badge';
import type useUnitProvider from '/@/core/context/unit';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { formatDate } from '/@/utils';
import { getHybridProductName } from '/@/utils/crop-monitoring';

const DetailRow = ({ label, items }: { label: string; items: { key: string | number; value: string }[] }) => (
    <Stack sx={{ width: '100%' }}>
        <Typography variant='caption' color='primary.mui.inactive'>
            {label}
        </Typography>
        {items.map((item) => (
            <Typography key={item.key} variant='subtitle1' fontWeight='bold' color='primary'>
                {item.value}
            </Typography>
        ))}
    </Stack>
);

const FieldSummaryCardMobile = ({
    field,
    cropMonitoring,
    units,
    canEditResource,
    onAddCrop,
}: {
    field?: Field;
    cropMonitoring?: CropMonitoring;
    units: ReturnType<typeof useUnitProvider>;
    canEditResource: boolean;
    onAddCrop: () => void;
}) => {
    const { open, toggle } = useToggle(false);

    const showAddCrop = !cropMonitoring?.active && canEditResource;

    // Multi-hybrid (CUL-1505, approach B): every hybrid is its own active CropMonitoring of the
    // campaign; the mobile summary stacks one value per hybrid (matching the desktop cards).
    const activeHybrids = (field?.activeCropMonitorings ?? []).filter((cm) => cm.campaign === cropMonitoring?.campaign);
    const hybrids = activeHybrids.length ? activeHybrids : cropMonitoring ? [cropMonitoring] : [];
    const rowItems = (value: (h: CropMonitoring) => string) =>
        hybrids.length ? hybrids.map((h) => ({ key: h.id, value: value(h) })) : [{ key: 'none', value: '-' }];

    return (
        <Stack
            sx={{
                bgcolor: 'grey.100',
                borderRadius: '8px',
                p: 2,
                width: '100%',
            }}
            spacing={2}
        >
            <Stack direction='row' spacing={4} sx={{ width: '100%' }}>
                <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='subtitle1' color='primary'>
                        {_('Lote')}
                    </Typography>
                    <Typography variant='h6' color='primary'>
                        {field?.name}
                    </Typography>
                    {field?.area ? (
                        <Typography variant='subtitle1' color='primary.mui.inactive'>
                            {convertAreaFromM2(field.area, units).toString()}
                        </Typography>
                    ) : null}
                </Stack>
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='subtitle1' color='primary'>
                        {_('Híbrido')}
                    </Typography>
                    {cropMonitoring?.active ? (
                        hybrids.map((h) => (
                            <Stack
                                key={h.id}
                                direction='row'
                                spacing={1}
                                alignItems='center'
                                justifyContent='space-between'
                                useFlexGap
                            >
                                <Stack>
                                    <Typography variant='subtitle1' color='primary'>
                                        {getHybridProductName(h)}
                                    </Typography>
                                    <HybridSeedName hybrid={h} />
                                </Stack>
                                {/* Negative margin cancels the card's p: 2 so the badge reaches the card edge. */}
                                {h.cornCropMonitoring?.isPreceon && <PreceonBadge sx={{ mr: -2 }} />}
                            </Stack>
                        ))
                    ) : showAddCrop ? (
                        <Link
                            sx={{ width: 'fit-content' }}
                            component='button'
                            underline='always'
                            variant='body2'
                            color='secondary'
                            onClick={onAddCrop}
                        >
                            {_('Agregar cultivo')}
                        </Link>
                    ) : null}
                </Stack>
            </Stack>

            <Stack spacing={1.5} sx={{ width: '100%' }}>
                <Stack
                    direction='row'
                    spacing={0.5}
                    alignItems='center'
                    onClick={() => toggle()}
                    sx={{ cursor: 'pointer', alignSelf: 'flex-start', color: 'primary.onSurface' }}
                >
                    <Typography variant='overline' fontWeight='bold' sx={{ color: 'inherit', lineHeight: '16px' }}>
                        {open ? _('Ocultar detalles') : _('Ver detalles')}
                    </Typography>
                    {open ? (
                        <ExpandLess sx={{ fontSize: 18, color: 'inherit' }} />
                    ) : (
                        <ExpandMore sx={{ fontSize: 18, color: 'inherit' }} />
                    )}
                </Stack>
                {open && (
                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                        <DetailRow
                            label={_('Fecha de siembra')}
                            items={rowItems((h) => (h.plantingDate ? formatDate(new Date(h.plantingDate)) : '-'))}
                        />
                        <DetailRow
                            label={_('Densidad')}
                            items={rowItems((h) =>
                                h.density ? units.toDensity(h.density, h.crop, 2, 'planting').toString() : '-'
                            )}
                        />
                        <DetailRow
                            label={_('Ambiente')}
                            items={rowItems((h) => units.toYield(h.environment, h.crop, 2).toString())}
                        />
                        <DetailRow label={_('Ubicación')} items={[{ key: 'loc', value: field?.location ?? '-' }]} />
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

export default FieldSummaryCardMobile;
